export const meridianStyles = `
@font-face {
  font-family: "GTF Good Mono";
  src: url("/fonts/GoodMono/GTFGoodMonoTRIAL-Thin.otf") format("opentype");
  font-weight: 100;
  font-display: swap;
}

@keyframes zmk-spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes zmk-blink {
  0%,
  49% {
    opacity: 1;
  }
  50%,
  100% {
    opacity: 0;
  }
}

@keyframes zmk-sheen {
  0% {
    background-position: 180% 0;
  }
  100% {
    background-position: -80% 0;
  }
}

@keyframes zmk-breathe {
  0%,
  100% {
    opacity: 0.82;
  }
  50% {
    opacity: 1;
  }
}

@keyframes zspin {
  to {
    transform: rotate(360deg);
  }
}

.zmk-root {
  --zmk-bg: var(--zmk-palette-bg);
  --zmk-footer: var(--zmk-palette-footer);
  --zmk-panel: var(--zmk-palette-panel);
  --zmk-card: var(--zmk-palette-card);
  --zmk-card-soft: var(--zmk-palette-card-soft);
  --zmk-ink: var(--zmk-palette-ink);
  --zmk-copy: color-mix(in srgb, var(--zmk-ink) 66%, transparent);
  --zmk-muted: color-mix(in srgb, var(--zmk-ink) 50%, transparent);
  --zmk-faint: color-mix(in srgb, var(--zmk-ink) 36%, transparent);
  --zmk-line: color-mix(in srgb, var(--zmk-ink) 12%, transparent);
  --zmk-line-soft: color-mix(in srgb, var(--zmk-ink) 8%, transparent);
  --zmk-blue: var(--zmk-palette-blue);
  --zmk-blue-hover: var(--zmk-palette-blue-hover);
  --zmk-gold: var(--zmk-palette-gold);
  --zmk-ease: cubic-bezier(0.16, 1, 0.3, 1);
  --zmk-cam-x: 0px;
  --zmk-cam-y: 0px;
  --zmk-cam-scale: 1;

  background: var(--zmk-bg);
  color: var(--zmk-ink);
  color-scheme: dark;
  font-family: var(--font-brand-stack);
  font-weight: 300;
  min-height: 100svh;
  overflow-x: clip;
  -webkit-font-smoothing: antialiased;
}

.zmk-root *,
.zmk-root *::before,
.zmk-root *::after {
  box-sizing: border-box;
}

.zmk-root a {
  color: inherit;
  text-decoration: none;
}

.zmk-root button,
.zmk-root input,
.zmk-root textarea,
.zmk-root select {
  font-family: var(--font-brand-stack);
}

.zmk-root ::selection {
  background: color-mix(in srgb, var(--zmk-blue) 35%, transparent);
  color: var(--zmk-ink);
}

.zmk-hero,
.zmk-work,
.zmk-security,
.zmk-faq,
.zmk-request {
  scroll-margin-top: 84px;
}

.zmk-display {
  color: var(--zmk-ink);
  font-family: var(--font-libre-caslon-display), Georgia, serif !important;
  font-weight: 400;
  letter-spacing: 0 !important;
  line-height: 1.08;
  margin: 0;
  text-wrap: balance;
}

.zmk-mono {
  color: var(--zmk-muted);
  font-family: "GTF Good Mono", monospace !important;
  font-size: 11px;
  font-weight: 100;
  letter-spacing: 0.18em;
  margin: 0;
  text-transform: uppercase;
}

.zmk-bloom,
.zmk-grain {
  inset: 0;
  pointer-events: none;
  position: absolute;
}

.zmk-bloom {
  background: var(--zmk-hero-bloom);
}

.zmk-bloom--work {
  background: radial-gradient(
    ellipse 60% 54% at 50% 46%,
    color-mix(in srgb, var(--zmk-palette-bloom-warm) 36%, transparent) 0%,
    color-mix(in srgb, var(--zmk-palette-bloom-shadow) 18%, transparent) 48%,
    color-mix(in srgb, var(--zmk-bg) 0%, transparent) 76%
  );
}

.zmk-bloom--request {
  animation: zmk-breathe 28s ease-in-out infinite;
  background: radial-gradient(
    ellipse 52% 42% at 50% 66%,
    color-mix(in srgb, var(--zmk-palette-bloom-warm) 46%, transparent) 0%,
    color-mix(in srgb, var(--zmk-palette-bloom-shadow) 24%, transparent) 44%,
    color-mix(in srgb, var(--zmk-bg) 0%, transparent) 74%
  );
}

.zmk-grain {
  background-image: var(--zmk-hero-grain);
  mix-blend-mode: overlay;
  opacity: 0.05;
}

.zmk-button {
  align-items: center;
  background: var(--zmk-blue) !important;
  border: 1px solid color-mix(in srgb, var(--ld-surface-white) 12%, transparent) !important;
  border-radius: 8px !important;
  box-shadow: 0 0 20px 1px color-mix(in srgb, var(--zmk-blue) 13%, transparent);
  color: var(--zmk-ink) !important;
  display: inline-flex;
  font-size: 16px !important;
  font-weight: 400 !important;
  gap: 10px;
  min-height: 48px !important;
  padding: 14px 30px !important;
  transition:
    filter 0.25s ease,
    transform 0.25s ease,
    box-shadow 0.2s ease !important;
}

.zmk-button:hover {
  box-shadow: 0 0 16px 1px color-mix(in srgb, var(--zmk-blue) 22%, transparent);
  filter: brightness(1.14);
  transform: translateY(-2px);
}

.zmk-outline-button,
.zmk-secondary-button,
.zmk-mini-action {
  background: transparent !important;
  border: 1px solid color-mix(in srgb, var(--zmk-ink) 22%, transparent) !important;
  border-radius: 8px !important;
  color: var(--zmk-ink) !important;
  font-weight: 400 !important;
  transition:
    border-color 0.2s ease,
    transform 0.2s ease,
    background-color 0.2s ease !important;
}

.zmk-outline-button:hover,
.zmk-secondary-button:hover,
.zmk-mini-action:hover {
  background: color-mix(in srgb, var(--zmk-ink) 4%, transparent) !important;
  border-color: color-mix(in srgb, var(--zmk-ink) 50%, transparent) !important;
  transform: translateY(-2px);
}

.zmk-secondary-button svg,
.zmk-button svg,
.zmk-text-link svg {
  height: 15px;
  width: 15px;
}

.zmk-nav {
  background: transparent;
  border-bottom: 1px solid transparent;
  inset-inline: 0;
  position: fixed;
  top: 0;
  transform: translateY(0);
  transition:
    background 0.3s ease,
    border-color 0.3s ease,
    transform 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  z-index: 100;
}

/* Values read from the reference tick handler: scrim at >80px, retract on
   downward scroll. */
.zmk-nav--scrolled {
  background: color-mix(in srgb, var(--zmk-bg) 86%, transparent);
  border-bottom-color: color-mix(in srgb, var(--ld-surface-white) 6%, transparent);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.zmk-nav--hidden {
  transform: translateY(-100%);
}

.zmk-nav__inner {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin: 0 auto;
  max-width: 1440px;
  padding: 20px 48px;
}

.zmk-nav__brand {
  align-items: center;
  display: flex;
}

.zmk-nav__brand img {
  display: block;
  height: 34px;
  object-fit: contain;
  width: auto;
}

.zmk-nav__links {
  align-items: center;
  display: flex;
  gap: 40px;
}

.zmk-nav__links a {
  color: var(--zmk-ink);
  font-size: 15px;
  font-weight: 400;
  letter-spacing: 0.01em;
  position: relative;
  transition: color 0.2s ease;
}

.zmk-nav__links a::after {
  background: color-mix(in srgb, var(--zmk-ink) 85%, transparent);
  bottom: -6px;
  content: "";
  height: 1.5px;
  left: 0;
  position: absolute;
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.18s ease;
  width: 100%;
}

.zmk-nav__links a:hover {
  color: color-mix(in srgb, var(--zmk-ink) 65%, transparent);
}

.zmk-nav__links a:hover::after {
  transform: scaleX(1);
}

.zmk-nav__request {
  background: transparent !important;
  border: 1px solid color-mix(in srgb, var(--zmk-ink) 28%, transparent) !important;
  border-radius: 8px !important;
  color: var(--zmk-ink) !important;
  font-size: 15px !important;
  font-weight: 400 !important;
  height: auto !important;
  padding: 10px 21px !important;
  transition:
    border-color 0.2s ease,
    transform 0.2s ease !important;
}

.zmk-nav__request:hover {
  border-color: color-mix(in srgb, var(--zmk-ink) 55%, transparent) !important;
  transform: translateY(-2px);
}

.zmk-nav__menu {
  color: var(--zmk-ink) !important;
  display: none !important;
  height: 44px !important;
  width: 44px !important;
}

.zmk-mobile-menu {
  background: color-mix(in srgb, var(--zmk-bg) 98%, transparent);
  backdrop-filter: blur(14px);
  display: none;
  flex-direction: column;
  inset: 0;
  overflow-y: auto;
  padding: 100px 24px 36px;
  position: fixed;
  z-index: 99;
}

.zmk-mobile-menu.is-open {
  display: flex;
}

.zmk-mobile-menu a {
  border-bottom: 1px solid color-mix(in srgb, var(--zmk-ink) 8%, transparent);
  color: var(--zmk-ink);
  font-family: var(--font-libre-caslon-display), Georgia, serif;
  font-size: 30px;
  font-weight: 300;
  letter-spacing: 0;
  padding: 18px 0;
}

.zmk-mobile-menu .zmk-button {
  margin-top: 32px;
  width: 100%;
}

.zmk-hero {
  align-items: center;
  background: var(--zmk-bg);
  display: flex;
  min-height: 100svh;
  overflow: hidden;
  position: relative;
}

.zmk-hero__inner {
  align-items: center;
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  max-width: 1040px;
  padding: 150px 48px 130px;
  position: relative;
  text-align: center;
  width: 100%;
}

.zmk-hero__title {
  font-size: 74px;
  font-weight: 700;
  max-width: 15ch;
}

.zmk-hero__cta {
  align-items: center;
  display: flex;
  flex-direction: column;
  margin-top: 26px;
}

.zmk-hero__cta p {
  color: color-mix(in srgb, var(--zmk-ink) 73%, transparent);
  font-size: 17px;
  font-weight: 300;
  line-height: 1.6;
  margin: 0 0 40px;
  max-width: 620px;
  text-wrap: pretty;
}

/* Entrance reveals and hairline draws stay on separate transforms so a
   visible line never inherits translateY, and a reveal never inherits scaleX. */
[data-sreveal] {
  opacity: 0;
  transform: translate3d(0, 16px, 0);
  transition:
    opacity 0.7s var(--zmk-ease),
    transform 0.7s var(--zmk-ease);
}

[data-sreveal].is-visible {
  opacity: 1;
  transform: translate3d(0, 0, 0);
}

/* Reference hairlines: 1px, left-origin scaleX 0→1 over 1.2s. Compositor-only. */
.zmk-sline,
.zmk-root [data-sline] {
  background: var(--zmk-line);
  display: block;
  height: 1px;
  transform: scaleX(0);
  transform-origin: left center;
  transition: transform 1.2s var(--zmk-ease);
  width: 100%;
}

.zmk-root [data-sline].is-visible {
  transform: scaleX(1);
}

.zmk-root [data-sline][data-delay="150"] {
  transition-delay: 0.15s;
}

.zmk-root [data-sline][data-delay="200"] {
  transition-delay: 0.2s;
}

.zmk-work {
  background: var(--zmk-bg);
  color: var(--zmk-ink);
  min-height: 100svh;
  overflow: hidden;
  position: relative;
}

.zmk-work__inner {
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: 0 auto;
  max-width: 1260px;
  min-height: 100svh;
  min-width: 0;
  padding: 106px 48px 72px;
  position: relative;
  width: 100%;
}

.zmk-work__header {
  margin-bottom: 30px;
  text-align: center;
}

.zmk-work__header .zmk-mono {
  margin-bottom: 22px;
}

.zmk-work__header .zmk-display {
  font-size: 48px;
  line-height: 1.1;
}

.zmk-tabs {
  align-items: baseline;
  display: flex;
  gap: 44px;
  margin-bottom: 26px;
}

.zmk-tab {
  background: transparent !important;
  border: 0 !important;
  border-radius: 0 !important;
  color: color-mix(in srgb, var(--zmk-ink) 46%, transparent) !important;
  display: flex !important;
  font-size: 15px !important;
  font-weight: 300 !important;
  gap: 10px;
  height: auto !important;
  padding: 0 0 10px !important;
  position: relative;
  transition: color 0.28s ease !important;
}

.zmk-tab > span:first-child {
  color: color-mix(in srgb, var(--zmk-ink) 34%, transparent);
  font-family: "GTF Good Mono", monospace;
  font-size: 11px;
  letter-spacing: 0.04em;
}

.zmk-tab i {
  background: var(--zmk-blue);
  bottom: 0;
  height: 1.5px;
  left: 0;
  opacity: 0;
  position: absolute;
  right: 0;
  transform: scaleX(0);
  transform-origin: left;
  transition:
    transform 0.28s var(--zmk-ease),
    opacity 0.28s ease;
}

.zmk-tab.is-active {
  color: var(--zmk-ink) !important;
}

.zmk-tab.is-active i {
  opacity: 1;
  transform: scaleX(1);
}

.zmk-demo-frame {
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
  position: relative;
  width: min(1040px, 100%);
}

.zmk-demo-frame__desktop {
  background: var(--zmk-palette-deep-surface);
  border: 1px solid color-mix(in srgb, var(--zmk-ink) 8%, transparent);
  border-radius: 16px;
  box-shadow:
    0 30px 80px -48px color-mix(in srgb, var(--zom-black) 90%, transparent),
    0 0 0 1px color-mix(in srgb, var(--ld-surface-white) 3%, transparent);
  isolation: isolate;
  max-width: 100%;
  overflow: hidden;
  position: relative;
  width: 100%;
}

.zmk-demo-frame__scale {
  isolation: isolate;
  max-width: 100%;
  overflow: hidden;
  position: relative;
  width: 100%;
}

.zmk-stage {
  background: var(--zmk-palette-deep-surface);
  height: 650px;
  left: 50%;
  margin-left: -520px;
  overflow: hidden;
  position: absolute;
  top: 0;
  transform-origin: top center;
  width: 1040px;
}

/* The wrapper is what travels: the reference lifts the composer to the centre
   of the stage for the ask, drops it back for the working phase, then hides it
   for the canvas. The controller writes these transforms. */
.zmk-stage [data-uc-card],
.zmk-stage [data-uc-done] {
  pointer-events: none;
  z-index: 3;
}

.zmk-stage [data-uc-canvas],
.zmk-stage [data-uc-email] {
  pointer-events: none;
  z-index: 2;
}

.zmk-stage__inputwrap {
  bottom: 26px;
  box-sizing: border-box;
  left: 50%;
  max-width: min(560px, calc(100% - 48px));
  min-width: 0;
  position: absolute;
  transform: translateX(-50%);
  transition:
    opacity 0.4s ease,
    transform 0.65s var(--zmk-ease);
  width: min(58%, calc(100% - 48px));
  z-index: 4;
}

.zmk-stage__input {
  align-items: flex-start;
  background: var(--zmk-product-input-bg);
  border: 1px solid color-mix(in srgb, var(--ld-surface-white) 9%, transparent);
  border-radius: 10px;
  box-shadow: 0 26px 70px -34px color-mix(in srgb, var(--zom-black) 75%, transparent);
  box-sizing: border-box;
  display: flex;
  gap: 4px;
  justify-content: flex-start;
  max-width: 100%;
  min-height: 52px;
  min-width: 0;
  overflow: hidden;
  padding: 13px 22px;
  transition: border-color 0.3s ease;
  width: 100%;
}

.zmk-stage__ph {
  color: color-mix(in srgb, var(--zmk-ink) 35%, transparent);
  flex: 0 1 auto;
  font-size: 14px;
  font-weight: 300;
  overflow: hidden;
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.zmk-stage__typed {
  color: color-mix(in srgb, var(--zmk-ink) 88%, transparent);
  flex: 1 1 auto;
  font-size: 14px;
  font-weight: 300;
  line-height: 1.45;
  min-width: 0;
  overflow-wrap: anywhere;
  transition: opacity 0.3s ease;
  white-space: pre-wrap;
  word-break: break-word;
}

.zmk-stage__caret {
  background: color-mix(in srgb, var(--zmk-ink) 80%, transparent);
  flex: none;
  height: 16px;
  margin-left: 1px;
  opacity: 0;
  width: 1.5px;
}

.zmk-reasoning,
.zmk-summary-card,
.zmk-canvas,
.zmk-done,
.zmk-email-panel {
  opacity: 0;
  pointer-events: none;
  position: absolute;
  transition:
    opacity 0.5s var(--zmk-ease),
    transform 0.5s var(--zmk-ease);
}

.zmk-reasoning {
  background: var(--zmk-panel);
  border: 1px solid color-mix(in srgb, var(--ld-surface-white) 8%, transparent);
  border-radius: 14px;
  box-shadow: 0 30px 70px -30px color-mix(in srgb, var(--zom-black) 70%, transparent);
  left: 50%;
  padding: 20px 26px 18px;
  top: 43%;
  transform: translate(-50%, -50%) scale(1.04);
  width: 604px;
  z-index: 3;
}

.zmk-reasoning__head {
  align-items: center;
  display: flex;
  gap: 6px;
  margin: 12px 0 5px;
}

.zmk-reasoning__head span {
  color: color-mix(in srgb, var(--zmk-ink) 38%, transparent);
  font-size: 10px;
  letter-spacing: 0.05em;
}

/* Three rows are visible at a time; the oldest collapses out of the top. */
.zmk-reasoning__rows {
  display: flex;
  flex-direction: column;
  gap: 7px;
  height: 78px;
  justify-content: flex-end;
  overflow: hidden;
}

.zmk-reasoning__text {
  color: color-mix(in srgb, var(--zmk-ink) 42%, transparent);
  font-size: 11.5px;
  font-weight: 300;
  line-height: 1.55;
  margin: 0 0 12px;
  max-width: 64ch;
  min-height: 36px;
}

/* One word, lit as it arrives. Applied and removed by the controller. */
.zmk-sheen {
  animation: zmk-sheen 0.85s linear 2;
  background: linear-gradient(
    100deg,
    color-mix(in srgb, var(--zmk-ink) 42%, transparent) 32%,
    color-mix(in srgb, var(--ld-surface-white) 92%, transparent) 50%,
    color-mix(in srgb, var(--zmk-ink) 42%, transparent) 68%
  );
  background-clip: text;
  background-size: 220% 100%;
  color: transparent;
}

.zmk-tool-row {
  align-items: center;
  display: flex;
  gap: 9px;
  max-height: 22px;
  min-height: 19px;
  opacity: 0;
  overflow: hidden;
  transform: translateY(6px);
  transition:
    opacity 0.18s ease,
    transform 0.18s ease,
    max-height 0.35s ease,
    margin 0.35s ease;
}

.zmk-tool-row.is-child {
  margin-left: 22px;
}

.zmk-tool-row__mark {
  align-items: center;
  display: flex;
  flex: none;
  height: 13px;
  justify-content: center;
  width: 13px;
}

.zmk-tool-row__mark::before {
  animation: zmk-spin 0.7s linear infinite;
  border: 1.5px solid color-mix(in srgb, var(--zmk-ink) 16%, transparent);
  border-radius: 50%;
  border-top-color: color-mix(in srgb, var(--zmk-ink) 80%, transparent);
  content: "";
  display: block;
  height: 11px;
  width: 11px;
}

/* Resolved: the spinner is replaced by a check. */
.zmk-tool-row__mark.is-done::before {
  animation: none;
  border: 0;
  border-bottom: 1.5px solid var(--zmk-blue-hover);
  border-radius: 0;
  border-right: 1.5px solid var(--zmk-blue-hover);
  height: 9px;
  margin-top: -2px;
  transform: rotate(45deg);
  width: 5px;
}

.zmk-tool-row__label {
  font-size: 12px;
  font-weight: 300;
}

.zmk-tool-row__label.is-done {
  color: color-mix(in srgb, var(--zmk-ink) 50%, transparent);
}

.zmk-tool-row__result {
  color: color-mix(in srgb, var(--zmk-ink) 33%, transparent);
  font-size: 11px;
  font-weight: 300;
  opacity: 0;
  transition: opacity 0.35s ease;
  white-space: nowrap;
}

.zmk-summary-card {
  background: var(--zmk-panel);
  border: 1px solid color-mix(in srgb, var(--ld-surface-white) 8%, transparent);
  border-radius: 14px;
  box-shadow: 0 30px 70px -30px color-mix(in srgb, var(--zom-black) 70%, transparent);
  left: 50%;
  padding: 24px 30px 22px;
  top: 45%;
  transform: translate(-50%, -50%) translateY(18px);
  width: 620px;
  z-index: 3;
}



.zmk-summary-card h3,
.zmk-done h3 {
  color: var(--zmk-ink);
  font-family: var(--font-libre-caslon-display), Georgia, serif;
  font-size: 24px;
  font-weight: 400;
  line-height: 1.25;
  margin: 10px 0 18px;
}

.zmk-summary-card__rows {
  border-top: 1px solid color-mix(in srgb, var(--ld-surface-white) 8%, transparent);
}

.zmk-summary-card__rows div {
  border-bottom: 1px solid color-mix(in srgb, var(--ld-surface-white) 8%, transparent);
  display: grid;
  gap: 18px;
  grid-template-columns: 118px 1fr auto;
  padding: 12px 0;
}

.zmk-summary-card__rows span {
  color: color-mix(in srgb, var(--zmk-ink) 40%, transparent);
  font-family: "GTF Good Mono", monospace;
  font-size: 8px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.zmk-summary-card__rows p {
  color: color-mix(in srgb, var(--zmk-ink) 82%, transparent);
  font-size: 12px;
  line-height: 1.4;
  margin: 0;
}

.zmk-summary-card__rows em {
  color: color-mix(in srgb, var(--zmk-ink) 42%, transparent);
  font-family: "GTF Good Mono", monospace;
  font-size: 10px;
  font-style: normal;
}

.zmk-summary-card .zmk-secondary-button {
  margin-top: 18px;
}

.zmk-canvas {
  background: var(--zmk-bg);
  display: flex;
  flex-direction: column;
  inset: 0;
  transform: scale(0.94);
  transform-origin: 50% 44%;
  z-index: 2;
}

.zmk-canvas__topbar {
  align-items: center;
  background: var(--zmk-palette-section);
  border-bottom: 1px solid color-mix(in srgb, var(--ld-surface-white) 6%, transparent);
  color: color-mix(in srgb, var(--zmk-ink) 56%, transparent);
  display: flex;
  flex: none;
  font-size: 12px;
  gap: 9px;
  height: 48px;
  justify-content: center;
}

.zmk-canvas__dot {
  background: var(--zmk-blue);
  border-radius: 50%;
  height: 7px;
  width: 7px;
}

.zmk-canvas__body {
  display: flex;
  flex: 1;
  min-height: 0;
}

.zmk-canvas__work {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  position: relative;
}

.zmk-canvas__scroll {
  padding: 22px 26px 26px;
  transform: translate(0, 0) scale(1);
  transform-origin: 0 0;
  transition: transform 1.2s cubic-bezier(0.35, 0, 0.2, 1);
  width: 760px;
}

.zmk-canvas__grid {
  display: grid;
  gap: 14px;
  grid-template-columns: 1.12fr 1fr;
}

.zmk-panel {
  background: var(--zmk-palette-inset-panel);
  border: 1px solid color-mix(in srgb, var(--ld-surface-white) 6%, transparent);
  border-radius: 10px;
  min-height: 150px;
  opacity: 0;
  padding: 18px;
  transform: translateY(10px);
  transition:
    opacity 0.46s var(--zmk-ease),
    transform 0.46s var(--zmk-ease),
    border-color 0.5s ease;
}

.zmk-panel:nth-child(5),
.zmk-panel:nth-child(6) {
  min-height: 190px;
}

.zmk-panel.is-blue {
  border-color: color-mix(in srgb, var(--zmk-blue-hover) 42%, transparent);
}

.zmk-panel.is-gold {
  border-color: color-mix(in srgb, var(--zmk-gold) 46%, transparent);
}

/* The metrics strip reveals cell by cell before the camera pans across it. */
.zmk-panel__cells {
  display: flex !important;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.zmk-panel__cells span {
  background: color-mix(in srgb, var(--zmk-ink) 5%, transparent);
  border: 1px solid color-mix(in srgb, var(--ld-surface-white) 6%, transparent);
  border-radius: 6px;
  color: color-mix(in srgb, var(--zmk-ink) 72%, transparent) !important;
  font-family: "GTF Good Mono", monospace;
  font-size: 10px !important;
  letter-spacing: 0.08em !important;
  margin-bottom: 0 !important;
  opacity: 0;
  padding: 5px 9px;
  transform: translateY(8px);
}

/* The staffing lever the sequence drags. A picture of a control, not one. */
.zmk-lever {
  display: block !important;
  margin-top: 14px;
}

.zmk-lever__track {
  background: color-mix(in srgb, var(--zmk-ink) 10%, transparent);
  border-radius: 999px;
  display: block;
  height: 5px;
  position: relative;
  width: 100%;
}

.zmk-lever__fill {
  background: var(--zmk-blue);
  border-radius: 999px;
  display: block;
  height: 100%;
  width: 62%;
}

.zmk-lever__dot {
  background: var(--zmk-ink);
  border: 2px solid var(--zmk-blue);
  border-radius: 50%;
  display: block;
  height: 13px;
  left: 62%;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 13px;
}

.zmk-lever__label {
  color: color-mix(in srgb, var(--zmk-ink) 42%, transparent) !important;
  display: block;
  font-size: 10px !important;
  letter-spacing: 0.06em !important;
  margin-top: 9px !important;
  margin-bottom: 0 !important;
}

.zmk-panel span {
  color: color-mix(in srgb, var(--zmk-ink) 42%, transparent);
  display: block;
  font-family: "GTF Good Mono", monospace;
  font-size: 9px;
  letter-spacing: 0.14em;
  margin-bottom: 9px;
  text-transform: uppercase;
}

.zmk-panel h4 {
  color: var(--zmk-ink);
  font-family: var(--font-libre-caslon-display), Georgia, serif;
  font-size: 23px;
  font-weight: 400;
  line-height: 1.16;
  margin: 0 0 10px;
}

.zmk-panel p {
  color: color-mix(in srgb, var(--zmk-ink) 64%, transparent);
  font-size: 13px;
  line-height: 1.56;
  margin: 0;
}

.zmk-panel em {
  color: color-mix(in srgb, var(--zmk-ink) 35%, transparent);
  display: block;
  font-family: "GTF Good Mono", monospace;
  font-size: 9px;
  font-style: normal;
  letter-spacing: 0.1em;
  margin-top: 16px;
  text-transform: uppercase;
}

.zmk-canvas__rail {
  background: var(--zmk-palette-deep-surface);
  border-left: 1px solid color-mix(in srgb, var(--ld-surface-white) 6%, transparent);
  display: flex;
  flex: none;
  flex-direction: column;
  min-width: 190px;
  padding: 16px;
  width: 24%;
}

.zmk-canvas__rail .zmk-mono {
  border-bottom: 1px solid color-mix(in srgb, var(--ld-surface-white) 6%, transparent);
  margin-bottom: auto;
  padding-bottom: 10px;
}

.zmk-chat-bubble {
  align-self: flex-end;
  background: var(--zmk-product-chat-bubble-bg);
  border-radius: 9px;
  color: color-mix(in srgb, var(--zmk-ink) 88%, transparent);
  font-size: 10px;
  font-weight: 300;
  line-height: 1.45;
  margin-bottom: 12px;
  max-width: 88%;
  padding: 8px 12px;
}

.zmk-canvas__rail > p:not(.zmk-mono) {
  color: color-mix(in srgb, var(--zmk-ink) 60%, transparent);
  font-size: 10px;
  line-height: 1.55;
  margin: 0 0 14px;
}

.zmk-mini-action {
  align-self: flex-start;
  font-size: 10.5px !important;
  height: auto !important;
  padding: 7px 13px !important;
}

.zmk-done {
  background: var(--zmk-panel);
  border: 1px solid color-mix(in srgb, var(--ld-surface-white) 8%, transparent);
  border-radius: 14px;
  box-shadow: 0 30px 70px -30px color-mix(in srgb, var(--zom-black) 70%, transparent);
  left: 50%;
  padding: 22px 28px 20px;
  top: 48%;
  transform: translate(-50%, -50%) translateY(18px);
  width: 620px;
  z-index: 5;
}

.zmk-done__head {
  align-items: center;
  border-bottom: 1px solid color-mix(in srgb, var(--ld-surface-white) 8%, transparent);
  display: flex;
  gap: 9px;
  padding-bottom: 13px;
}

.zmk-done__head span {
  background: var(--zmk-blue);
  border-radius: 50%;
  height: 7px;
  width: 7px;
}

.zmk-done__head p {
  color: color-mix(in srgb, var(--zmk-ink) 55%, transparent);
  font-family: "GTF Good Mono", monospace;
  font-size: 9px;
  letter-spacing: 0.14em;
  margin: 0;
  text-transform: uppercase;
}

.zmk-done__runs {
  border-top: 1px solid color-mix(in srgb, var(--ld-surface-white) 8%, transparent);
  margin-bottom: 16px;
}

.zmk-canvas__answer {
  color: color-mix(in srgb, var(--zmk-ink) 60%, transparent);
  font-size: 12px;
  line-height: 1.5;
}

.zmk-canvas__answer.is-deferred {
  filter: blur(7px);
  opacity: 0;
  transition:
    opacity 0.7s var(--zmk-ease),
    filter 0.7s var(--zmk-ease);
}

.zmk-run {
  align-items: center;
  border-bottom: 1px solid color-mix(in srgb, var(--ld-surface-white) 8%, transparent);
  display: grid;
  gap: 14px;
  grid-template-columns: auto 1fr auto;
  padding: 9px 0;
}

.zmk-run span {
  color: color-mix(in srgb, var(--zmk-ink) 45%, transparent);
  font-family: "GTF Good Mono", monospace;
  font-size: 12px;
}

.zmk-run p {
  color: color-mix(in srgb, var(--zmk-ink) 78%, transparent);
  font-size: 12px;
  line-height: 1.35;
  margin: 0;
}

.zmk-run em {
  color: color-mix(in srgb, var(--zmk-ink) 38%, transparent);
  font-family: "GTF Good Mono", monospace;
  font-size: 9px;
  font-style: normal;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  transition: color 0.3s ease;
}

.zmk-run__led {
  border-radius: 50%;
  display: block;
  height: 7px;
  width: 7px;
}

.zmk-run__led[data-tone="queued"] {
  background: color-mix(in srgb, var(--zmk-ink) 28%, transparent);
}

.zmk-run__led[data-tone="running"] {
  animation: zmk-spin 0.7s linear infinite;
  background: none;
  border: 1.5px solid color-mix(in srgb, var(--zmk-ink) 16%, transparent);
  border-top-color: color-mix(in srgb, var(--zmk-ink) 85%, transparent);
  height: 11px;
  width: 11px;
}

.zmk-run__led[data-tone="done"] {
  background: var(--zmk-blue-hover);
}

/* Gold, not blue: these two steps stop at the advisor's approval gate. */
.zmk-run__led[data-tone="gated"] {
  background: none;
  border: 1.5px solid var(--zmk-gold);
}

.zmk-run em[data-tone="running"] {
  color: color-mix(in srgb, var(--zmk-ink) 80%, transparent);
}

.zmk-run em[data-tone="done"] {
  color: color-mix(in srgb, var(--zmk-ink) 50%, transparent);
}

.zmk-run em[data-tone="gated"] {
  color: var(--zmk-gold);
}

.zmk-run.is-complete em {
  color: var(--zmk-gold);
}

.zmk-done > p {
  color: color-mix(in srgb, var(--zmk-ink) 68%, transparent);
  font-size: 13px;
  line-height: 1.6;
  margin: 0 0 18px;
}

.zmk-email-panel {
  background: var(--zmk-palette-section);
  border: 1px solid color-mix(in srgb, var(--ld-surface-white) 8%, transparent);
  border-radius: 14px;
  box-shadow: 0 30px 70px -30px color-mix(in srgb, var(--zom-black) 70%, transparent);
  left: 50%;
  padding: 22px;
  top: 50%;
  transform: translate(-50%, -50%) scale(0.985);
  width: 620px;
  z-index: 6;
}

.zmk-email-panel__banner {
  align-items: center;
  background: color-mix(in srgb, var(--zmk-blue) 12%, transparent);
  border: 1px solid color-mix(in srgb, var(--zmk-blue) 28%, transparent);
  border-radius: 9px;
  color: color-mix(in srgb, var(--zmk-ink) 86%, transparent);
  display: flex;
  font-size: 12px;
  gap: 8px;
  margin-bottom: 16px;
  padding: 10px 12px;
}

.zmk-email-panel__banner svg {
  color: var(--zmk-blue-hover);
  height: 14px;
  width: 14px;
}

.zmk-email-panel label {
  color: color-mix(in srgb, var(--zmk-ink) 42%, transparent);
  display: block;
  font-family: "GTF Good Mono", monospace;
  font-size: 9px;
  letter-spacing: 0.12em;
  margin-bottom: 10px;
  text-transform: uppercase;
}

.zmk-email-panel label span,
.zmk-email-panel__body {
  background: var(--zmk-palette-deep-panel);
  border: 1px solid color-mix(in srgb, var(--zmk-ink) 10%, transparent);
  border-radius: 8px;
  color: color-mix(in srgb, var(--zmk-ink) 76%, transparent);
  display: block;
  font-family: var(--font-brand-stack);
  font-size: 13px;
  font-weight: 300;
  letter-spacing: 0;
  line-height: 1.55;
  margin-top: 6px;
  padding: 10px 12px;
  text-transform: none;
}

.zmk-email-panel__body {
  margin-bottom: 16px;
}

.zmk-email-panel__body p {
  margin: 0 0 10px;
}

.zmk-email-panel__body p:last-child {
  margin-bottom: 0;
}

/* The cursor travels on the outer element and presses on the inner one, so a
   click pulse never fights the in-flight travel transform. */
.zmk-cursor {
  filter: drop-shadow(0 1px 2px color-mix(in srgb, var(--zom-black) 50%, transparent));
  left: 0;
  opacity: 0;
  pointer-events: none;
  position: absolute;
  top: 0;
  transform: translate(560px, 320px);
  transition:
    transform 0.7s cubic-bezier(0.3, 0.65, 0.25, 1),
    opacity 0.3s ease;
  z-index: 20;
}

.zmk-cursor__inner {
  display: block;
  transform: scale(1);
  transform-origin: 2px 2px;
  transition: transform 0.13s ease;
}

.zmk-mobile-demo {
  display: none;
}

.zmk-section-shell {
  margin: 0 auto;
  max-width: 1320px;
  padding: 138px 48px 126px;
}

.zmk-security {
  background: var(--zmk-bg);
  border-top: 1px solid color-mix(in srgb, var(--zmk-ink) 6%, transparent);
}

.zmk-security .zmk-mono {
  margin-bottom: 36px;
}

.zmk-security .zmk-display {
  font-size: 64px;
  max-width: 760px;
}

.zmk-security__intro {
  color: color-mix(in srgb, var(--zmk-ink) 68%, transparent);
  font-size: 18px;
  font-weight: 300;
  line-height: 1.6;
  margin: 26px 0 52px;
  max-width: 760px;
  text-wrap: pretty;
}

.zmk-security__grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.zmk-security__cta {
  margin-top: 100px;
}

.zmk-security__cta-row {
  align-items: center;
  display: flex;
  justify-content: space-between;
  gap: 64px;
  padding: 38px 0;
}

.zmk-security__grid article {
  border-left: 1px solid color-mix(in srgb, var(--zmk-ink) 10%, transparent);
  min-height: 190px;
  padding: 52px 36px 8px;
}

.zmk-security__grid article:first-child {
  border-left: 0;
  padding-left: 0;
}

.zmk-security__grid span {
  color: color-mix(in srgb, var(--zmk-ink) 42%, transparent);
  display: block;
  font-family: "GTF Good Mono", monospace;
  font-size: 10px;
  letter-spacing: 0.14em;
  margin-bottom: 22px;
  text-transform: uppercase;
}

.zmk-security__grid h3 {
  color: var(--zmk-ink);
  font-family: var(--font-libre-caslon-display), Georgia, serif;
  font-size: 26px;
  font-weight: 400;
  letter-spacing: 0;
  line-height: 1.2;
  margin: 0 0 14px;
}

.zmk-security__grid p {
  color: color-mix(in srgb, var(--zmk-ink) 58%, transparent);
  font-size: 15px;
  line-height: 1.62;
  margin: 0;
}

.zmk-text-link {
  align-items: baseline;
  color: var(--zmk-ink) !important;
  display: inline-flex;
  font-size: 15px;
  font-weight: 400;
  gap: 7px;
  margin-top: 0;
  text-decoration: underline !important;
  text-decoration-color: color-mix(in srgb, var(--zmk-ink) 40%, transparent) !important;
  text-decoration-thickness: 1px !important;
  text-underline-offset: 5px;
}

.zmk-faq {
  background: var(--zmk-bg);
  border-top: 1px solid color-mix(in srgb, var(--zmk-ink) 6%, transparent);
}

.zmk-faq__grid {
  display: grid;
  gap: 74px;
  grid-template-columns: 0.78fr 1.22fr;
  margin: 0 auto;
  max-width: 1320px;
  padding: 130px 48px 120px;
}

.zmk-faq__head {
  align-self: start;
  position: sticky;
  top: 120px;
}

.zmk-faq__head .zmk-mono {
  margin-bottom: 34px;
}

.zmk-faq__head .zmk-display {
  font-size: 52px;
  margin-bottom: 22px;
}

.zmk-faq__head > p:last-child {
  color: color-mix(in srgb, var(--zmk-ink) 60%, transparent);
  font-size: 17px;
  line-height: 1.6;
  margin: 0;
  max-width: 380px;
}

.zmk-faq__items {
  border-top: 1px solid color-mix(in srgb, var(--zmk-ink) 12%, transparent);
}

.zmk-faq__item {
  border-bottom: 1px solid color-mix(in srgb, var(--zmk-ink) 12%, transparent);
}

.zmk-faq__toggle {
  align-items: center;
  background: transparent !important;
  border: 0 !important;
  border-radius: 0 !important;
  color: color-mix(in srgb, var(--zmk-ink) 88%, transparent) !important;
  display: flex !important;
  font-size: 19px !important;
  font-weight: 400 !important;
  gap: 40px;
  height: auto !important;
  justify-content: space-between;
  line-height: 1.4;
  min-height: 82px;
  padding: 26px 0 !important;
  text-align: left;
  width: 100%;
}

.zmk-faq__toggle span {
  flex: 1 1 auto;
  min-width: 0;
  overflow-wrap: anywhere;
  white-space: normal;
}

.zmk-faq__toggle i {
  flex: none;
  height: 13px;
  position: relative;
  width: 13px;
}

.zmk-faq__toggle i::before,
.zmk-faq__toggle i::after {
  background: var(--zmk-blue-hover);
  content: "";
  position: absolute;
}

.zmk-faq__toggle i::before {
  height: 1px;
  left: 0;
  top: 6px;
  width: 13px;
}

.zmk-faq__toggle i::after {
  height: 13px;
  left: 6px;
  top: 0;
  transition: transform 0.24s cubic-bezier(0.4, 0, 0.2, 1);
  width: 1px;
}

.zmk-faq__toggle[aria-expanded="true"] i::after {
  transform: scaleY(0);
}

.zmk-faq__body {
  display: grid;
  grid-template-rows: 0fr;
  opacity: 0;
  transition:
    grid-template-rows 0.44s var(--zmk-ease),
    opacity 0.24s ease;
}

.zmk-faq__body.is-open {
  grid-template-rows: 1fr;
  opacity: 1;
}

.zmk-faq__body-inner {
  min-height: 0;
  overflow: hidden;
  transform: translateY(-4px);
  transition: transform 0.44s var(--zmk-ease);
}

.zmk-faq__body.is-open .zmk-faq__body-inner {
  transform: translateY(0);
}

.zmk-faq__body p {
  color: color-mix(in srgb, var(--zmk-ink) 62%, transparent);
  font-size: 16px;
  font-weight: 300;
  line-height: 1.68;
  margin: 0;
  max-width: 620px;
  padding-bottom: 30px;
  text-wrap: pretty;
}

.zmk-faq__bottom {
  align-items: center;
  display: flex;
  gap: 40px;
  justify-content: space-between;
  padding: 44px 0 0;
}

.zmk-faq__bottom p {
  color: color-mix(in srgb, var(--zmk-ink) 55%, transparent);
  font-size: 16px;
  font-weight: 300;
  margin: 0;
}

.zmk-request {
  background: var(--zmk-bg);
  color: var(--zmk-ink);
  overflow: hidden;
  position: relative;
}

.zmk-request__inner {
  margin: 0 auto;
  max-width: 760px;
  padding: 210px 48px 230px;
  position: relative;
  text-align: center;
}

.zmk-request .zmk-display {
  font-size: 54px;
}

.zmk-request p {
  color: color-mix(in srgb, var(--zmk-ink) 66%, transparent);
  font-size: 18px;
  font-weight: 300;
  line-height: 1.6;
  margin: 24px auto 48px;
  max-width: 560px;
  text-wrap: pretty;
}

.zmk-footer {
  background: var(--zmk-footer);
  color: var(--zmk-ink);
}

.zmk-footer__inner {
  margin: 0 auto;
  max-width: 1440px;
  padding: 80px 48px 40px;
}

.zmk-footer__grid {
  align-items: start;
  display: grid;
  gap: 64px;
  grid-template-columns: minmax(0, 1fr) auto;
  padding-bottom: 64px;
}

.zmk-footer__brand {
  max-width: 360px;
}

.zmk-footer__brand img {
  display: block;
  height: 30px;
  margin-bottom: 20px;
  object-fit: contain;
  width: auto;
}

.zmk-footer__brand p {
  color: color-mix(in srgb, var(--zmk-ink) 55%, transparent);
  font-size: 15px;
  font-weight: 300;
  line-height: 1.55;
  margin: 0;
}

.zmk-footer__links {
  display: flex;
  gap: 88px;
}

.zmk-footer__links div {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.zmk-footer__links span {
  color: color-mix(in srgb, var(--zmk-ink) 40%, transparent);
  font-family: "GTF Good Mono", monospace;
  font-size: 10px;
  letter-spacing: 0.14em;
  margin-bottom: 4px;
  text-transform: uppercase;
}

.zmk-footer__links a,
.zmk-footer__links button {
  background: transparent;
  border: 0;
  color: color-mix(in srgb, var(--zmk-ink) 72%, transparent);
  cursor: pointer;
  font-size: 15px;
  font-weight: 300;
  padding: 0;
  text-align: left;
  transition: color 0.2s ease;
}

.zmk-footer__links a:hover,
.zmk-footer__links button:hover {
  color: var(--zmk-ink);
}

.zmk-footer__base {
  border-top: 1px solid color-mix(in srgb, var(--zmk-ink) 10%, transparent);
  color: color-mix(in srgb, var(--zmk-ink) 42%, transparent);
  display: flex;
  flex-direction: column;
  font-size: 13px;
  font-weight: 300;
  gap: 24px;
  padding-top: 32px;
}

.zmk-footer__disclaimer {
  color: color-mix(in srgb, var(--zmk-ink) 50%, transparent);
  font-size: 12px;
  line-height: 1.55;
  margin: 0;
  text-wrap: pretty;
  width: 100%;
}

@media (max-width: 1023px) {
  .zmk-nav__links {
    display: none;
  }

  .zmk-nav__menu {
    display: inline-flex !important;
  }

  .zmk-hero__title {
    font-size: 64px;
  }

  .zmk-work__header .zmk-display {
    font-size: 48px;
  }

  .zmk-security .zmk-display,
  .zmk-request .zmk-display {
    font-size: 54px;
  }

  .zmk-faq__grid {
    gap: 48px;
    grid-template-columns: minmax(0, 1fr);
  }

  .zmk-faq__head {
    position: static;
  }

  .zmk-faq__head > p:last-child {
    max-width: 100%;
  }
}

@media (max-width: 767px) {
  .zmk-nav__inner {
    padding: 16px 20px;
  }

  .zmk-nav__brand img {
    height: 28px;
  }

  .zmk-hero {
    text-align: center;
  }

  .zmk-hero__inner {
    align-items: center;
    padding: 124px 24px 88px;
    text-align: center;
  }

  .zmk-hero__title {
    font-size: 42px;
    max-width: 14ch;
    text-wrap: balance;
  }

  .zmk-hero__cta {
    align-items: center;
    width: 100%;
  }

  .zmk-hero__cta p {
    margin-left: auto;
    margin-right: auto;
    max-width: 36ch;
  }

  .zmk-button {
    justify-content: center;
    width: 100%;
  }

  .zmk-work__inner {
    align-items: stretch;
    min-height: 0;
    padding: 96px 16px 64px;
  }

  .zmk-work__header {
    margin-bottom: 18px;
    padding-inline: 0;
    text-align: center;
  }

  .zmk-work__header .zmk-mono {
    margin-bottom: 14px;
  }

  .zmk-work__header .zmk-display {
    font-size: clamp(30px, 8.2vw, 36px);
    margin-inline: auto;
    max-width: 12ch;
    overflow-wrap: anywhere;
    text-wrap: balance;
  }

  .zmk-tabs {
    display: grid;
    gap: 8px;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    justify-items: center;
    margin-bottom: 18px;
    padding: 0;
    width: 100%;
  }

  .zmk-tab {
    align-items: center !important;
    flex-direction: column !important;
    font-size: 12px !important;
    gap: 4px !important;
    justify-content: flex-start !important;
    max-width: 100%;
    min-width: 0;
    padding: 0 2px 8px !important;
    white-space: normal !important;
  }

  .zmk-tab > span:first-child {
    font-size: 10px;
  }

  .zmk-tab [data-zwlabel] {
    text-align: center;
    text-wrap: balance;
  }

  .zmk-demo-frame {
    margin-inline: 0;
    max-width: 100%;
    width: 100%;
  }

  .zmk-demo-frame__desktop {
    display: block;
  }

  .zmk-stage__inputwrap {
    max-width: min(560px, calc(100% - 40px));
    width: min(58%, calc(100% - 40px));
  }

  .zmk-stage__input {
    padding: 12px 16px;
  }

  .zmk-stage__typed,
  .zmk-stage__ph {
    max-width: 100%;
  }

  /* Keep the static card for assistive tech; the live stage is aria-hidden. */
  .zmk-mobile-demo {
    background: transparent;
    border: 0;
    clip: rect(0 0 0 0);
    clip-path: inset(50%);
    display: block;
    height: 1px;
    overflow: hidden;
    padding: 0;
    position: absolute;
    white-space: nowrap;
    width: 1px;
  }

  .zmk-section-shell {
    padding: 88px 24px 72px;
  }

  .zmk-security .zmk-display {
    font-size: 42px;
  }

  .zmk-security__grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .zmk-security__intro {
    margin-bottom: 36px;
  }

  .zmk-security__grid article {
    border-left: 0;
    border-top: 1px solid color-mix(in srgb, var(--zmk-ink) 10%, transparent);
    min-height: 0;
    padding: 40px 0 0;
  }

  .zmk-security__grid article:first-child {
    border-top: 0;
  }

  .zmk-faq__grid {
    padding: 94px 24px 90px;
  }

  .zmk-faq__head .zmk-display {
    font-size: 40px;
  }

  .zmk-faq__toggle {
    font-size: 17px !important;
    gap: 24px;
  }

  .zmk-faq__bottom {
    align-items: stretch;
    flex-direction: column;
    gap: 20px;
  }

  .zmk-request__inner {
    padding: 132px 24px 140px;
  }

  .zmk-request .zmk-display {
    font-size: 42px;
  }

  .zmk-footer__inner {
    padding: 56px 24px 40px;
  }

  .zmk-footer__grid {
    gap: 44px;
    grid-template-columns: minmax(0, 1fr);
    padding-bottom: 48px;
  }

  .zmk-footer__links {
    flex-wrap: wrap;
    gap: 40px 48px;
  }

  /* Shorter hairline draw on phones: same language, less compositor time. */
  .zmk-root [data-sline] {
    transition-duration: 0.9s;
  }

  .zmk-security__cta {
    margin-top: 64px;
  }

  .zmk-security__cta-row {
    padding: 28px 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .zmk-root *,
  .zmk-root *::before,
  .zmk-root *::after {
    animation-duration: 0.001ms !important;
    animation-iteration-count: 1 !important;
    scroll-behavior: auto !important;
    transition-duration: 0.001ms !important;
  }

  .zmk-root [data-sreveal],
  .zmk-root [data-sline] {
    opacity: 1;
    transform: none;
  }
}
`;
